import { t } from "@lingui/macro";
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowsOutCardinal,
  CircleNotch,
  ClockClockwise,
  CubeFocus,
  FilePdf,
  FloppyDisk,
  LineSegment,
  LinkSimple,
  MagnifyingGlass,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  PaintBrushHousehold,
} from "@phosphor-icons/react";
import { Button, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SignupModal } from "@/client/components/auth/signup-modal";
import { PdfLoadingModal } from "@/client/components/pdf-loading-modal";
import { useGuestResume } from "@/client/hooks/use-guest-resume";
import { useTemplateAccess } from "@/client/hooks/use-template-access";
import { useToast } from "@/client/hooks/use-toast";
import { usePrintResume, usePrintResumeTest } from "@/client/services/resume/print";
import { useUser } from "@/client/services/user/user";
import { useAuthStore } from "@/client/stores/auth";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { forceDownload } from "@/client/utils/download";
import { trackDownloadResume } from "@/client/utils/gtm";
import subscriptionLogger from "@/client/utils/subscription-logger";

// Add these lines to create a leftPanelMode state in the builder store
useBuilderStore.setState({ leftPanelMode: "content" });

export const BuilderToolbar = () => {
  const { toast } = useToast();
  const [pdfSteps, setPdfSteps] = useState<
    {
      id: string;
      title: string;
      description: string;
      status: "pending" | "in-progress" | "completed" | "error";
      progress?: number;
    }[]
  >([
    {
      id: "1",
      title: t`Preparing Resume`,
      description: t`Gathering resume data...`,
      status: "pending",
    },
    {
      id: "2",
      title: t`Generating PDF`,
      description: t`Converting to PDF format...`,
      status: "pending",
      progress: 0,
    },
    {
      id: "3",
      title: t`Finalizing Export`,
      description: t`Preparing download...`,
      status: "pending",
    },
    {
      id: "4",
      title: t`Download Ready`,
      description: t`PDF generation complete!`,
      status: "pending",
    },
  ]);
  const navigate = useNavigate();
  const { saveToLocalStorage, downloadAsJson } = useGuestResume();

  const updatePdfStep = (stepId: string, updates: Partial<(typeof pdfSteps)[0]>) => {
    setPdfSteps((steps) =>
      steps.map((step) => (step.id === stepId ? { ...step, ...updates } : step)),
    );
  };

  const [panMode, setPanMode] = useState<boolean>(true);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authModalType, setAuthModalType] = useState<"save" | "download">("save");
  const [showPdfLoadingModal, setShowPdfLoadingModal] = useState<boolean>(false);

  const { user, loading: userLoading } = useUser();
  const subscriptionTier = useAuthStore((state) => state.subscriptionTier);
  const isLoggedIn = !!user;

  // Get setLeftPanelMode function from the builder store
  const setLeftPanelMode = useBuilderStore((state) => state.setLeftPanelMode);
  const setActiveTab = useBuilderStore((state) => state.setActiveTab);
  // Get current panel mode
  const leftPanelMode = useBuilderStore((state) => state.leftPanelMode);

  // Log user subscription tier information on component mount
  useEffect(() => {
    subscriptionLogger.logComponentRender(t`BuilderToolbar`, subscriptionTier);
  }, [subscriptionTier]);

  const { id, title, visibility } = useResumeStore((state) => state.resume);
  const setValue = useResumeStore((state) => state.setValue);
  const undo = useResumeStore.temporal.getState().undo;
  const redo = useResumeStore.temporal.getState().redo;
  const frameRef = useBuilderStore((state) => state.frame.ref);

  // Check if resume is public
  const isPublic = visibility === "public";

  // Get basic user data for prefilling the signup form
  const userData = useResumeStore((state) => {
    try {
      return {
        name: state.resume.data.basics.name || "",
        email: state.resume.data.basics.email || "",
      };
    } catch {
      return {
        name: "",
        email: "",
      };
    }
  });

  const pageOptions = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.page.options || { breakLine: false, pageNumbers: false };
    } catch {
      // Default values if path is incomplete
      return { breakLine: false, pageNumbers: false };
    }
  });

  const { printResume, loading } = usePrintResume();
  const { printResumeTest, loading: testLoading } = usePrintResumeTest();

  // Get template access permissions
  const templateTheme = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.theme;
    } catch {
      return {};
    }
  });
  const { canDownloadPdf } = useTemplateAccess(templateTheme);

  const onSave = () => {
    if (!isLoggedIn) {
      setAuthModalType("save");
      setShowAuthModal(true);
      return;
    }

    // For logged-in users, the resume is automatically saved
    // through the debouncedUpdateResume in the store
    toast({
      variant: "default",
      title: t`Resume Saved`,
      description: t`Your resume has been saved successfully.`,
    });
  };

  const templateName = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.template;
    } catch {
      return undefined;
    }
  });

  const onPrint = async () => {
    if (!isLoggedIn) {
      trackDownloadResume({
        resumeId: id,
        format: "pdf",
        templateName,
        outcome: "redirect_to_signup",
      });
      setAuthModalType("download");
      setShowAuthModal(true);
      return;
    }

    // Check template access permissions (this replaces the old FREE tier check)
    if (!canDownloadPdf) {
      trackDownloadResume({
        resumeId: id,
        format: "pdf",
        templateName,
        outcome: "redirect_to_pricing",
      });

      // Navigate to the pricing page
      navigate("/pricing");
      return;
    }

    // Reset PDF steps and show loading modal
    setPdfSteps([
      {
        id: "1",
        title: "Preparing Resume",
        description: "Gathering resume data...",
        status: "pending",
      },
      {
        id: "2",
        title: "Generating PDF",
        description: "Converting to PDF format...",
        status: "pending",
        progress: 0,
      },
      {
        id: "3",
        title: "Finalizing Export",
        description: "Preparing download...",
        status: "pending",
      },
      {
        id: "4",
        title: "Download Ready",
        description: "PDF generation complete!",
        status: "pending",
      },
    ]);
    setShowPdfLoadingModal(true);

    // Use setTimeout to ensure state update and re-render happens
    void setTimeout(async () => {
      try {
        // Step 1: Preparing Resume
        updatePdfStep("1", { status: "in-progress" });
        await new Promise((resolve) => setTimeout(resolve, 800));
        updatePdfStep("1", { status: "completed" });

        // Step 2: Generating PDF with progress
        updatePdfStep("2", { status: "in-progress", progress: 0 });

        // Simulate PDF generation progress
        const progressPromise = (async () => {
          for (let i = 0; i <= 90; i += 15) {
            updatePdfStep("2", { progress: i });
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        })();

        // Start actual PDF generation
        const startTime = Date.now();
        const pdfPromise = printResume({ id });

        // Wait for both progress animation and PDF generation
        const [, { url }] = await Promise.all([progressPromise, pdfPromise]);

        // Complete step 2
        updatePdfStep("2", { progress: 100, status: "completed" });

        // Step 3: Finalizing
        updatePdfStep("3", { status: "in-progress" });

        // Ensure minimum display time
        const elapsedTime = Date.now() - startTime;
        const minimumDisplayTime = 3000; // 3 seconds minimum for better UX
        if (elapsedTime < minimumDisplayTime) {
          await new Promise((resolve) => setTimeout(resolve, minimumDisplayTime - elapsedTime));
        }

        updatePdfStep("3", { status: "completed" });

        // Step 4: Download Ready
        updatePdfStep("4", { status: "in-progress" });
        await new Promise((resolve) => setTimeout(resolve, 500));
        updatePdfStep("4", { status: "completed" });

        // Force download the PDF
        await forceDownload(url, `${title}-resume.pdf`);

        trackDownloadResume({
          resumeId: id,
          format: "pdf",
          templateName,
          outcome: "success",
        });

        // Brief delay to show completion
        await new Promise((resolve) => setTimeout(resolve, 800));
        setShowPdfLoadingModal(false);

        toast({
          variant: "default",
          title: t`PDF Downloaded Successfully!`,
          description: t`Your resume PDF has been downloaded to your device.`,
        });
      } catch {
        // Mark current step as error
        const currentStep = pdfSteps.find((step) => step.status === "in-progress");
        if (currentStep) {
          updatePdfStep(currentStep.id, { status: "error" });
        }

        setTimeout(() => {
          setShowPdfLoadingModal(false);
        }, 2000);

        toast({
          variant: "error",
          title: t`Error`,
          description: t`Failed to generate PDF. Please try again.`,
        });
      }
    }, 100); // Small delay to ensure modal renders
  };

  // New function to handle test PDF export without subscription check
  const onTestPdfExport = async () => {
    // Show loading modal immediately
    setShowPdfLoadingModal(true);

    // Use setTimeout to ensure state update and re-render happens
    void setTimeout(async () => {
      try {
        // Ensure minimum display time for loading modal
        const startTime = Date.now();
        const { url } = await printResumeTest({ id });
        const elapsedTime = Date.now() - startTime;
        const minimumDisplayTime = 2000; // 2 seconds minimum

        if (elapsedTime < minimumDisplayTime) {
          await new Promise((resolve) => setTimeout(resolve, minimumDisplayTime - elapsedTime));
        }

        await forceDownload(url, `${title}-resume-test.pdf`);
        setShowPdfLoadingModal(false);

        toast({
          variant: "default",
          title: t`PDF Export Test`,
          description: t`PDF has been downloaded successfully for testing.`,
        });
      } catch {
        setShowPdfLoadingModal(false);
        toast({
          variant: "error",
          title: t`Error`,
          description: t`Failed to generate PDF. Please try again.`,
        });
      }
    }, 100); // Small delay to ensure modal renders
  };

  const onCopy = async () => {
    if (!isLoggedIn) {
      setAuthModalType("save");
      setShowAuthModal(true);
      return;
    }
    // Show loading modal immediately
    setShowPdfLoadingModal(true);

    // Use setTimeout to ensure state update and re-render happens
    setTimeout(async () => {
      try {
        // Ensure minimum display time for loading modal
        const startTime = Date.now();
        const { url } = await printResume({ id });
        const elapsedTime = Date.now() - startTime;
        const minimumDisplayTime = 2000; // 2 seconds minimum

        if (elapsedTime < minimumDisplayTime) {
          await new Promise((resolve) => setTimeout(resolve, minimumDisplayTime - elapsedTime));
        }

        setShowPdfLoadingModal(false);
        if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          await navigator.clipboard.writeText(url);
          toast({
            variant: "default",
            title: t`A link has been copied to your clipboard.`,
            description: t`Anyone with this link can view and download the resume. Share it on your profile or with recruiters.`,
          });
        } else {
          // Fallback: try to use execCommand or show error
          try {
            const input = document.createElement("input");
            input.value = url;
            document.body.append(input);
            input.select();
            document.execCommand("copy");
            input.remove();
            toast({
              variant: "default",
              title: t`A link has been copied to your clipboard.`,
              description: t`Anyone with this link can view and download the resume. Share it on your profile or with recruiters.`,
            });
          } catch {
            toast({
              variant: "error",
              title: t`Copy to clipboard is not supported in this browser.`,
              description: t`Please copy the link manually.`,
            });
          }
        }
      } catch {
        setShowPdfLoadingModal(false);
        toast({
          variant: "error",
          title: t`Error`,
          description: t`Failed to copy link. Please try again.`,
        });
      }
    }, 100); // Small delay to ensure modal renders
  };

  const onZoomIn = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_IN" }, "*");
  const onZoomOut = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_OUT" }, "*");
  const onResetView = () => frameRef?.contentWindow?.postMessage({ type: "RESET_VIEW" }, "*");
  const onCenterView = () => frameRef?.contentWindow?.postMessage({ type: "CENTER_VIEW" }, "*");
  const onTogglePanMode = () => {
    setPanMode(!panMode);
    frameRef?.contentWindow?.postMessage({ type: "TOGGLE_PAN_MODE", panMode: !panMode }, "*");
  };

  const onToggleOption = (option: "breakLine" | "pageNumbers", value: boolean) => {
    try {
      setValue(`metadata.page.options.${option}`, value);
    } catch {
      // Silent fail
    }
  };

  const onCloseModal = () => {
    setShowAuthModal(false);
  };

  return (
    <>
      <motion.div
        className="relative z-10 hidden text-center md:inline-flex md:justify-center"
      >
        <div className="flex justify-center">
          <div className="inline-flex h-12 items-center justify-center gap-1 rounded-full border border-gray-200/80 bg-white px-3 shadow-[0_4px_24px_rgba(0,0,0,0.10)]">

            {/* Design Mode — labeled coral pill on far left */}
            <Tooltip content={t`Design Mode`}>
              <button
                className={cn(
                  "flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-all",
                  leftPanelMode === "design"
                    ? "bg-[#c46b42] text-white shadow-sm"
                    : "bg-[#c46b42]/10 text-[#c46b42] hover:bg-[#c46b42]/20",
                )}
                onClick={() => { setLeftPanelMode("design"); setActiveTab("design"); }}
              >
                <PaintBrushHousehold size={15} weight={leftPanelMode === "design" ? "fill" : "regular"} />
                {t`Design`}
              </button>
            </Tooltip>

            {/* Separator */}
            <div className="mx-1.5 h-5 w-px shrink-0 bg-gray-200" />

            {/* Undo */}
            <Tooltip content={t`Undo`}>
              <Button
                size="icon"
                variant="ghost"
                className="size-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => { undo(); }}
              >
                <ArrowCounterClockwise size={16} />
              </Button>
            </Tooltip>

            {/* Redo */}
            <Tooltip content={t`Redo`}>
              <Button
                size="icon"
                variant="ghost"
                className="size-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => { redo(); }}
              >
                <ArrowClockwise size={16} />
              </Button>
            </Tooltip>

            {/* Separator */}
            <div className="mx-1.5 h-5 w-px shrink-0 bg-gray-200" />

            {/* Pan mode — teal filled circle when active */}
            <Tooltip content={panMode ? t`Scroll to Pan` : t`Scroll to Zoom`}>
              <button
                className={cn(
                  "flex size-8 items-center justify-center rounded-full transition-all",
                  panMode
                    ? "bg-[#268599] text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                )}
                onClick={onTogglePanMode}
              >
                {panMode ? <ArrowsOutCardinal size={16} /> : <MagnifyingGlass size={16} />}
              </button>
            </Tooltip>

            {/* Zoom Out */}
            <Tooltip content={t`Zoom Out`}>
              <Button size="icon" variant="ghost" className="size-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700" onClick={onZoomOut}>
                <MagnifyingGlassMinus size={16} />
              </Button>
            </Tooltip>

            {/* Zoom In */}
            <Tooltip content={t`Zoom In`}>
              <Button size="icon" variant="ghost" className="size-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700" onClick={onZoomIn}>
                <MagnifyingGlassPlus size={16} />
              </Button>
            </Tooltip>

            {/* Reset Zoom */}
            <Tooltip content={t`Reset Zoom`}>
              <Button size="icon" variant="ghost" className="size-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700" onClick={onResetView}>
                <ClockClockwise size={16} />
              </Button>
            </Tooltip>

            {/* Center Artboard */}
            <Tooltip content={t`Center Artboard`}>
              <Button size="icon" variant="ghost" className="size-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700" onClick={onCenterView}>
                <CubeFocus size={16} />
              </Button>
            </Tooltip>

            {/* Separator */}
            <div className="mx-1.5 h-5 w-px shrink-0 bg-gray-200" />

            {/* Page Break Line */}
            <Tooltip content={t`Toggle Page Break Line`}>
              <button
                className={cn(
                  "flex size-8 items-center justify-center rounded-full transition-all",
                  pageOptions.breakLine
                    ? "bg-gray-100 text-gray-700"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                )}
                onClick={() => { onToggleOption("breakLine", !pageOptions.breakLine); }}
              >
                <LineSegment size={16} />
              </button>
            </Tooltip>

            {/* Save */}
            <Tooltip content={t`Save Resume`}>
              <Button size="icon" variant="ghost" className="size-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700" onClick={onSave}>
                <FloppyDisk size={16} />
              </Button>
            </Tooltip>

            {/* Hidden copy link button — kept for feature parity */}
            <Button
              size="icon"
              variant="ghost"
              className="hidden"
              disabled={!isPublic}
              onClick={onCopy}
            >
              <LinkSimple size={16} />
            </Button>

            {/* Separator */}
            <div className="mx-1.5 h-5 w-px shrink-0 bg-gray-200" />

            {/* Download PDF — coral filled circle */}
            <Tooltip content={t`Download PDF`}>
              <button
                disabled={loading}
                className={cn(
                  "flex size-9 items-center justify-center rounded-full font-semibold text-white transition-all",
                  loading && "opacity-60",
                  canDownloadPdf
                    ? "bg-[#c46b42] shadow-sm hover:bg-[#b05c35]"
                    : "bg-[#c46b42]/70 hover:bg-[#c46b42]",
                )}
                onClick={onPrint}
              >
                {loading ? (
                  <CircleNotch size={16} className="animate-spin" />
                ) : (
                  <FilePdf size={16} weight="bold" />
                )}
              </button>
            </Tooltip>

          </div>
        </div>
      </motion.div>

      <SignupModal
        isOpen={showAuthModal}
        transferGuestResume
        prefilledEmail={userData.email}
        onClose={onCloseModal}
      />

      {/* PDF Loading Modal */}
      <PdfLoadingModal isOpen={showPdfLoadingModal} steps={pdfSteps} />
    </>
  );
};
